/**
 * 护理方案 Hook
 * 集成 TanStack Query 和 Zustand Store
 * Requirements: 11.1, 13.4
 */

import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCarePlanStore } from "@/stores/carePlanStore";
import { carePlanService } from "@/services/carePlanService";
import type { ObservationStatus } from "@/types/care-plan";

// Query Keys
export const carePlanKeys = {
  all: ["carePlan"] as const,
  assessment: (id: string) => [...carePlanKeys.all, "assessment", id] as const,
  careLogs: (assessmentId: string) =>
    [...carePlanKeys.all, "careLogs", assessmentId] as const,
};

interface UseCarePlanOptions {
  assessmentId: string;
  enabled?: boolean;
}

export function useCarePlan({
  assessmentId,
  enabled = true,
}: UseCarePlanOptions) {
  const queryClient = useQueryClient();
  const store = useCarePlanStore();

  // 获取评估详情（包含护理时间轴）
  const assessmentQuery = useQuery({
    queryKey: carePlanKeys.assessment(assessmentId),
    queryFn: () => carePlanService.getAssessmentWithTimeline(assessmentId),
    enabled: enabled && !!assessmentId,
    staleTime: 30 * 1000, // 30 秒
  });

  // 使用 useMemo 缓存 timeline，避免不必要的引用变化
  const timelineFromQuery = useMemo(
    () => assessmentQuery.data?.care_timeline,
    [assessmentQuery.data?.care_timeline],
  );

  // 当 timeline 数据变化时，同步到 store
  // 使用 JSON.stringify 进行深度比较，避免引用变化导致的误判
  useEffect(() => {
    if (timelineFromQuery) {
      const currentTimeline = store.timeline;

      // 如果没有 timeline 或者 timeline 真的变化了，才更新
      if (
        !currentTimeline ||
        JSON.stringify(currentTimeline) !== JSON.stringify(timelineFromQuery)
      ) {
        // 使用 setTimeout 将更新推迟到下一个事件循环
        // 避免在 render 阶段触发状态更新
        const timeoutId = setTimeout(() => {
          store.setTimeline(timelineFromQuery);
        }, 0);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [timelineFromQuery]); // 只依赖 timelineFromQuery

  // 处理评估数据错误
  useEffect(() => {
    if (assessmentQuery.isError && assessmentQuery.error) {
      const errorMessage = assessmentQuery.error.message;
      // 只在错误信息真的变化时才更新
      if (store.error !== errorMessage) {
        // 使用 setTimeout 将更新推迟到下一个事件循环
        // 避免在 render 阶段触发状态更新
        const timeoutId = setTimeout(() => {
          store.setError(errorMessage);
        }, 0);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [assessmentQuery.isError, assessmentQuery.error?.message]); // 移除 store 依赖，只依赖 error.message

  // 获取护理日志
  const careLogsQuery = useQuery({
    queryKey: carePlanKeys.careLogs(assessmentId),
    queryFn: () => carePlanService.getCareLogs(assessmentId),
    enabled: enabled && !!assessmentId && store.isUnlocked(),
    staleTime: 10 * 1000, // 10 秒
  });

  // 当护理日志加载成功时,更新 store
  // 使用 useMemo 缓存数据，避免不必要的引用变化
  const careLogsFromQuery = useMemo(
    () => careLogsQuery.data?.care_logs,
    [careLogsQuery.data?.care_logs],
  );
  const careLogsMetaFromQuery = useMemo(
    () => careLogsQuery.data?.meta,
    [careLogsQuery.data?.meta],
  );

  useEffect(() => {
    if (careLogsQuery.isSuccess && careLogsFromQuery && careLogsMetaFromQuery) {
      // 使用深度比较避免不必要的更新
      const currentLogs = store.careLogs;
      const currentMeta = store.careLogsMeta;

      // 只有在数据真正变化时才更新
      const logsChanged =
        !currentLogs ||
        currentLogs.length !== careLogsFromQuery.length ||
        JSON.stringify(currentLogs) !== JSON.stringify(careLogsFromQuery);

      const metaChanged =
        !currentMeta ||
        JSON.stringify(currentMeta) !== JSON.stringify(careLogsMetaFromQuery);

      if (logsChanged || metaChanged) {
        // 使用 setTimeout 将更新推迟到下一个事件循环
        // 避免在 render 阶段触发状态更新
        const timeoutId = setTimeout(() => {
          store.setCareLogs(careLogsFromQuery, careLogsMetaFromQuery);
        }, 0);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [careLogsQuery.isSuccess, careLogsFromQuery, careLogsMetaFromQuery]); // 移除 store 依赖

  // 解锁护理方案
  const unlockMutation = useMutation({
    mutationFn: (paymentToken?: string) =>
      carePlanService.unlockCare(assessmentId, paymentToken),
    onMutate: () => {
      store.setUnlocking(true);
    },
    onSuccess: (data) => {
      // 只更新 timeline,不刷新查询
      // 避免触发连锁反应
      store.setTimeline(data.care_timeline);
    },
    onError: (error: Error) => {
      store.setError(error.message);
    },
    onSettled: () => {
      store.setUnlocking(false);
    },
  });

  // 开始护理
  const startCareMutation = useMutation({
    mutationFn: () => carePlanService.startCare(assessmentId),
    onMutate: () => {
      store.setStarting(true);
    },
    onSuccess: (data) => {
      // 只更新 timeline,不刷新查询
      store.setTimeline(data.care_timeline);
    },
    onError: (error: Error) => {
      store.setError(error.message);
    },
    onSettled: () => {
      store.setStarting(false);
    },
  });

  // 完成护理
  const completeCareMutation = useMutation({
    mutationFn: (
      outcome: "recovered" | "improved" | "unchanged" | "lost" | "unknown",
    ) => carePlanService.completeCare(assessmentId, outcome),
    onMutate: () => {
      store.setCompleting(true);
    },
    onSuccess: (data) => {
      store.setTimeline(data.care_timeline);
      queryClient.invalidateQueries({
        queryKey: carePlanKeys.careLogs(assessmentId),
      });
    },
    onError: (error: Error) => {
      store.setError(error.message);
    },
    onSettled: () => {
      store.setCompleting(false);
    },
  });

  // 记录任务完成 - 乐观更新
  // Requirements: 4.6, 4.7
  const completeTaskMutation = useMutation({
    mutationFn: async ({
      taskId,
      dayNumber,
    }: {
      taskId: string;
      dayNumber: number;
    }) => {
      // 检查是否为后备任务 ID (以 "sop-" 或 "fallback-" 开头)
      // 这些任务 ID 是前端生成的，后端无法识别，因此不需要调用 API
      if (taskId.startsWith("sop-") || taskId.startsWith("fallback-")) {
        console.log("Mocking API call for fallback task:", taskId);
        return {
          care_log: {
            id: Date.now(), // 生成临时 ID
            log_type: "task_completed" as const,
            observation_status: null,
            day_number: dayNumber,
            task_data: { task_id: taskId },
            notes: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          meta: null, // 不更新 meta
        };
      }

      return carePlanService.recordTaskCompleted(
        assessmentId,
        taskId,
        dayNumber,
      );
    },

    // 乐观更新：API 调用前立即更新 UI
    onMutate: async ({ taskId }) => {
      // 取消正在进行的查询
      await queryClient.cancelQueries({
        queryKey: carePlanKeys.careLogs(assessmentId),
      });

      // 保存当前状态用于回滚
      const previousCompletedTasks = new Set(store.completedTaskIds);

      // 乐观更新 UI - 立即打勾
      store.markTaskCompleted(taskId);

      return { previousCompletedTasks, taskId };
    },

    // API 成功：更新服务端返回的数据
    onSuccess: (data) => {
      // 后备任务虽然没有真正调用 API，但我们模拟了返回数据
      store.addCareLog(data.care_log);
      if (data.meta) {
        store.setCareLogs(store.careLogs, data.meta);
      }
    },

    // API 失败：回滚 UI 状态
    onError: (_err, { taskId }, context) => {
      console.error("记录任务失败:", _err);

      // 回滚到之前的状态
      if (context?.previousCompletedTasks) {
        store.markTaskIncomplete(taskId);
      }
      toast.error("记录失败，请稍后重试");
    },
  });

  // 取消任务完成
  const undoCompleteTaskMutation = useMutation({
    mutationFn: async ({ taskId }: { taskId: string }) => {
      // 1. 查找对应的日志 - 只取第一条，避免重复删除
      const logToDelete = store.careLogs.find(
        (l) =>
          l.log_type === "task_completed" &&
          (l.task_data as any)?.task_id === taskId,
      );

      if (!logToDelete) {
        // 如果找不到日志（可能是还未同步，或者是 fallback 任务刷新丢失了）
        // 只能做本地清理
        if (taskId.startsWith("sop-") || taskId.startsWith("fallback-")) {
          console.log("Mocking undo for fallback task:", taskId);
          return { deletedId: null };
        }
        // 不抛出错误，而是作为"已删除"处理，确保 UI 状态一致性
        console.warn("无法找到对应的完成记录，视为已删除:", taskId);
        return { deletedId: null };
      }

      // 2. 如果是 fallback 任务，直接返回成功
      if (taskId.startsWith("sop-") || taskId.startsWith("fallback-")) {
        console.log("Mocking API undo for fallback task:", taskId);
        return { deletedId: logToDelete.id };
      }

      // 3. 调用 API 删除日志（只删除一条）
      try {
        await carePlanService.deleteCareLog(assessmentId, logToDelete.id);
      } catch (error: any) {
        // 如果日志不存在（404），视为已删除成功
        if (error.status === 404 || error.code === "NOT_FOUND") {
          console.warn("CareLog already deleted:", logToDelete.id);
          return { deletedId: logToDelete.id };
        }
        throw error;
      }

      return { deletedId: logToDelete.id };
    },

    // 乐观更新
    onMutate: async ({ taskId }) => {
      await queryClient.cancelQueries({
        queryKey: carePlanKeys.careLogs(assessmentId),
      });

      const previousCompletedTasks = new Set(store.completedTaskIds);

      // 立即取消打勾
      store.markTaskIncomplete(taskId);

      return { previousCompletedTasks, taskId };
    },

    // 成功后移除本地日志
    onSuccess: ({ deletedId }, { taskId }) => {
      if (deletedId && deletedId > 0) {
        store.removeCareLog(deletedId);
      }

      // 再次强制确保 UI 状态正确 (防止中间有并发 update 覆盖了状态)
      store.markTaskIncomplete(taskId);

      // 延迟清除 pending 状态，确保 query 刷新后不会恢复
      // 延迟时间足够 query invalidate 和 refetch 完成
      setTimeout(() => {
        store.clearPendingRemoval(taskId);
      }, 500);

      // 延迟触发重新计算 meta，避免立即刷新覆盖乐观更新
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: carePlanKeys.careLogs(assessmentId),
        });
      }, 100);
    },

    onError: (_err, { taskId }, context) => {
      console.error("取消任务失败:", _err);
      if (context?.previousCompletedTasks) {
        store.markTaskCompleted(taskId); // 回滚
      }
      toast.error("取消失败，请重试");
    },
  });

  // 记录每日观察
  const recordObservationMutation = useMutation({
    mutationFn: ({
      status,
      dayNumber,
      notes,
    }: {
      status: ObservationStatus;
      dayNumber: number;
      notes?: string;
    }) =>
      carePlanService.recordObservation(assessmentId, status, dayNumber, notes),
    onSuccess: (data) => {
      store.addCareLog(data.care_log);
      if (data.meta) {
        store.setCareLogs(store.careLogs, data.meta);
      }
      // 检查升级警报
      if (data.escalation_alert?.triggered) {
        store.setEscalationAlert(data.escalation_alert);
      }
    },
  });

  // 便捷方法
  const unlock = useCallback(
    (paymentToken?: string) => unlockMutation.mutateAsync(paymentToken),
    [unlockMutation],
  );

  const startCare = useCallback(
    () => startCareMutation.mutateAsync(),
    [startCareMutation],
  );

  const completeCare = useCallback(
    (outcome: "recovered" | "improved" | "unchanged" | "lost" | "unknown") =>
      completeCareMutation.mutateAsync(outcome),
    [completeCareMutation],
  );

  const completeTask = useCallback(
    (taskId: string, dayNumber: number) => {
      // 使用 mutate 而非 mutateAsync，让乐观更新立即生效
      // 错误处理在 onError 中完成
      completeTaskMutation.mutate({ taskId, dayNumber });
    },
    [completeTaskMutation],
  );

  const undoCompleteTask = useCallback(
    (taskId: string) => {
      // 使用 mutate 而非 mutateAsync，让乐观更新立即生效
      // 错误处理在 onError 中完成
      undoCompleteTaskMutation.mutate({ taskId });
    },
    [undoCompleteTaskMutation],
  );

  const recordObservation = useCallback(
    (status: ObservationStatus, dayNumber: number, notes?: string) =>
      recordObservationMutation.mutateAsync({ status, dayNumber, notes }),
    [recordObservationMutation],
  );

  const dismissEscalationAlert = useCallback(() => {
    store.setEscalationAlert(null);
  }, [store]);

  const isTaskCompleted = useCallback(
    (taskId: string) => store.completedTaskIds.has(taskId),
    [store.completedTaskIds],
  );

  return {
    // 数据
    timeline: store.timeline,
    careLogs: store.careLogs,
    careLogsMeta: store.careLogsMeta,
    escalationAlert: store.escalationAlert,
    assessment: assessmentQuery.data, // 暴露完整的评估数据

    // 状态
    isLoading: assessmentQuery.isLoading || store.isLoading,
    isUnlocking: store.isUnlocking,
    isStarting: store.isStarting,
    isCompleting: store.isCompleting,
    error: store.error || assessmentQuery.error?.message,

    // 计算属性
    isUnlocked: store.isUnlocked(),
    isCareStarted: store.isCareStarted(),
    isCareCompleted: store.isCareCompleted(),
    currentDay: store.currentDay(),
    completedTaskIds: store.completedTaskIds,

    // 方法
    unlock,
    startCare,
    completeCare,
    completeTask,
    undoCompleteTask,
    recordObservation,
    dismissEscalationAlert,
    isTaskCompleted,

    // 刷新
    refetch: () => {
      assessmentQuery.refetch();
      if (store.isUnlocked()) {
        careLogsQuery.refetch();
      }
    },
  };
}

export default useCarePlan;
